import { supabase } from '@/integrations/supabase/client';
import type { PostIdea } from '@/components/chat/IdeaCards';

export interface OutlineGenerationOptions {
  idea: PostIdea;
  currentWebsite?: { id: string; url: string };
  messages: any[];
  setMessages: (messages: any[] | ((prev: any[]) => any[])) => void;
  setInput: (input: string) => void;
  setSending: (sending: boolean) => void;
}

export const generateOutlineForIdea = async (options: OutlineGenerationOptions): Promise<void> => {
  const { idea, currentWebsite, messages, setMessages, setInput, setSending } = options;
  
  // Send a message to create an outline for this idea
  const outlineMessage = `Create an outline for: ${idea.title}`;
  setInput(outlineMessage);
  
  // Trigger send automatically
  setTimeout(async () => {
    if (!outlineMessage.trim()) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: outlineMessage }]);
    setSending(true);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      
      // Gather website analysis data from localStorage
      const websiteAnalysisData = {
        website_content: JSON.parse(localStorage.getItem('website_content') || '[]'),
        website_info: JSON.parse(localStorage.getItem('website_info') || '{}'),
        key_content_pages: JSON.parse(localStorage.getItem('key_content_pages') || '[]'),
        post_ideas: JSON.parse(localStorage.getItem('post_ideas') || '[]'),
        language: localStorage.getItem('website_language') || localStorage.getItem('language') || 'en',
        offering: localStorage.getItem('website_offering') || '',
        goal: localStorage.getItem('website_goal') || ''
      };

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat-onboarding`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionData?.session?.access_token ? { 'Authorization': `Bearer ${sessionData.session.access_token}` } : {}),
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          website_id: currentWebsite?.id,
          website_url: currentWebsite?.url || localStorage.getItem('onboardingWebsite') || undefined,
          message: outlineMessage,
          history: messages.map(m => ({ role: m.role, content: m.content })),
          website_analysis_data: websiteAnalysisData
        })
      });

      const data = await response.json();
      const reply: string = data?.reply || 'Sorry, something went wrong.';

      // Process the response to handle mode indicators
      if (reply.startsWith('WRITE_MODE:')) {
        // Write mode: parse JSON after mode indicator
        const jsonContent = reply.replace(/^WRITE_MODE:\s*/, '');
        try {
          const parsed = JSON.parse(jsonContent);
          if (parsed.post) {
            // Format as post draft for display
            setMessages((m) => [...m, { 
              role: 'assistant', 
              content: JSON.stringify({ 
                _type: 'post_draft', 
                post: { 
                  title: parsed.post.title, 
                  intro: parsed.post.body, 
                  digest: parsed.post.digest 
                } 
              }) 
            }]);
          } else {
            // Fallback to chat if JSON parsing fails
            setMessages((m) => [...m, { role: 'assistant', content: jsonContent }]);
          }
        } catch (e) {
          // Fallback to chat if JSON parsing fails
          setMessages((m) => [...m, { role: 'assistant', content: jsonContent }]);
        }
      } else if (reply.startsWith('CHAT_MODE:')) {
        // Chat mode: normal conversation text (remove mode indicator)
        const chatContent = reply.replace(/^CHAT_MODE:\s*/, '');
        setMessages((m) => [...m, { role: 'assistant', content: chatContent }]);
      } else {
        // Default: add the AI response as-is
        setMessages((m) => [...m, { role: 'assistant', content: reply }]);
      }
    } catch (error) {
      console.error('Error generating outline:', error);
      setMessages((m) => [...m, { role: 'assistant', content: 'Sorry, I could not generate an outline. Please try again.' }]);
    } finally {
      setSending(false);
    }
  }, 100);
};
